<!--
	ScannerPicks: the EINSTAR scanners we send installers to (content/scanners.ts).
	`full` is a shelf with both product cards; `compact` is one inline card for a
	step in a guide, the Rockit with the Rigil as a text link. Every link is our
	short link (/go/<id>) tagged with `placement`, which redirects through Awin
	to the store, and the disclosure ships with it.

	Always the light Workbench palette, whatever section it sits in: the product
	renders are black and need a light stage. The tokens are set on the root
	instead of through `.wb--light`, because pages key the body colour off that
	class.

	Partner codes: the server renders every code in the data. Once mounted, the
	component drops any whose last day has passed, and content-gates stops a
	build that would still ship one.
-->
<script lang="ts">
	import { onMount } from 'svelte';
	import { trackEvent } from '$lib/analytics';
	import {
		affiliateNote,
		affiliateNoteShort,
		codeIsLive,
		codeUntilLabel,
		scannerPicks,
		shortHref,
		type AffiliatePlacement,
		type PartnerCode,
		type ScannerPick
	} from '$lib/content/scanners';

	interface Props {
		placement: AffiliatePlacement;
		variant?: 'full' | 'compact';
	}

	let { placement, variant = 'full' }: Props = $props();

	const [rockit, rigil] = scannerPicks;

	// Null until mounted, so the first client render matches the prerendered page.
	let now = $state<number | null>(null);
	onMount(() => {
		now = Date.now();
	});
	const showCode = (code: PartnerCode | undefined): code is PartnerCode =>
		!!code && (now === null || codeIsLive(code, now));

	let copied = $state<string | null>(null);
	async function copy(code: string) {
		try {
			await navigator.clipboard.writeText(code);
			copied = code;
			setTimeout(() => {
				if (copied === code) copied = null;
			}, 1800);
		} catch {
			// The code is printed on the button, so it can still be typed.
		}
	}

	function track(pick: ScannerPick) {
		trackEvent('Affiliate Click', { props: { product: pick.id, placement } }, 'affiliate_click');
	}
</script>

{#snippet arrow()}
	<svg class="arrow" viewBox="0 0 16 16" width="14" height="14" aria-hidden="true">
		<path
			d="M4.5 11.5 11.5 4.5M5.5 4.5h6v6"
			fill="none"
			stroke="currentColor"
			stroke-width="1.6"
			stroke-linecap="round"
			stroke-linejoin="round"
		/>
	</svg>
{/snippet}

{#snippet badge(pick: ScannerPick)}
	<span class="badge" data-tier={pick.tier}>
		<span class="badge__dot" aria-hidden="true"></span>{pick.badge}
	</span>
{/snippet}

{#snippet partnerCode(code: PartnerCode)}
	<div class="code">
		<span class="code__label">Partner code</span>
		<span class="code__field">
			<code class="code__value">{code.code}</code>
			<button type="button" class="code__copy" onclick={() => copy(code.code)}>
				{copied === code.code ? 'Copied' : 'Copy'}<span class="sr-only"> {code.code}</span>
			</button>
		</span>
		<span class="code__until">until {codeUntilLabel(code)}</span>
	</div>
{/snippet}

{#snippet newTab()}<span class="sr-only"> (opens in a new tab)</span>{/snippet}

{#if variant === 'full'}
	<div class="picks pk">
		<div class="picks__bar">
			<img
				class="picks__logo"
				src="/img/einstar-logo.webp"
				alt="EINSTAR"
				width={501}
				height={80}
				loading="lazy"
				decoding="async"
			/>
			<span class="picks__store">Official store</span>
			<span class="picks__mesh" aria-hidden="true">Mesh out · STL · OBJ · PLY</span>
		</div>

		<ul class="picks__grid">
			{#each scannerPicks as pick, i (pick.id)}
				<li class="pick">
					<div class="pick__stage">
						{@render badge(pick)}
						<span class="pick__n" aria-hidden="true">{String(i + 1).padStart(2, '0')}</span>
						<span class="pick__marks" aria-hidden="true"><i></i><i></i><i></i><i></i></span>
						<img
							class="pick__img"
							src={pick.image.src}
							alt={pick.image.alt}
							width={pick.image.w}
							height={pick.image.h}
							loading="lazy"
							decoding="async"
						/>
						<span class="pick__laser" aria-hidden="true"></span>
					</div>

					<div class="pick__body">
						<p class="pick__role">{pick.role}</p>
						<h3 class="pick__name"><span>{pick.brand}</span> {pick.model}</h3>
						<p class="pick__blurb">{pick.blurb}</p>
						<dl class="pick__specs">
							{#each pick.specs as s (s.k)}
								<div>
									<dt>{s.k}</dt>
									<dd>{s.v}</dd>
								</div>
							{/each}
						</dl>
						{#if showCode(pick.code)}
							{@render partnerCode(pick.code)}
						{/if}
						<a
							class="cta pick__cta"
							class:cta--fill={pick.tier === 'tested'}
							href={shortHref(pick, placement)}
							target="_blank"
							rel="sponsored noopener"
							onclick={() => track(pick)}
						>
							See the {pick.model} at EINSTAR {@render arrow()}{@render newTab()}
						</a>
					</div>
				</li>
			{/each}
		</ul>

		<p class="picks__note">{affiliateNote} Specs and codes are EINSTAR’s.</p>
	</div>
{:else}
	<aside class="mini pk" aria-label="The scanners we use">
		<div class="mini__stage">
			<img
				src={rockit.image.src}
				alt={rockit.image.alt}
				width={rockit.image.w}
				height={rockit.image.h}
				loading="lazy"
				decoding="async"
			/>
		</div>
		<div class="mini__body">
			<p class="mini__top">
				<img
					class="mini__logo"
					src="/img/einstar-logo.webp"
					alt="EINSTAR"
					width={501}
					height={80}
					loading="lazy"
					decoding="async"
				/>
				{@render badge(rockit)}
			</p>
			<p class="mini__name">{rockit.brand} {rockit.model}, a scanner in our studio</p>
			<p class="mini__text">
				Its scans were flattened, cut and installed with PPF Engine. Scanning big panels every day?
				The <a
					href={shortHref(rigil, placement)}
					target="_blank"
					rel="sponsored noopener"
					onclick={() => track(rigil)}>{rigil.brand} {rigil.model}{@render newTab()}</a
				> is the step up, and we use it in our studio too.
			</p>
			{#if showCode(rockit.code)}
				{@render partnerCode(rockit.code)}
			{/if}
			<p class="mini__fine">{affiliateNoteShort}</p>
		</div>
		<a
			class="cta cta--fill mini__cta"
			href={shortHref(rockit, placement)}
			target="_blank"
			rel="sponsored noopener"
			onclick={() => track(rockit)}
		>
			See the {rockit.model}
			{@render arrow()}{@render newTab()}
		</a>
	</aside>
{/if}

<p class="sr-only" role="status">{copied ? `Copied ${copied}` : ''}</p>

<style>
	/* Workbench light values (theme.css, .wb--light) plus EINSTAR's blue for the laser. */
	.pk {
		--pk-surface: #ffffff;
		--pk-band: #f5f5f0;
		--pk-line: #e6e6df;
		--pk-line2: #d2d2c8;
		--pk-ink: #0e0f0c;
		--pk-text: #35362f;
		--pk-muted: #5f6158;
		--pk-accent: #4f6d1c;
		--pk-fill: #b7f04c;
		--pk-fill-hover: #a6e23a;
		--pk-grid: rgba(14, 15, 12, 0.055);
		--pk-hover: rgba(14, 15, 12, 0.045);
		--pk-laser: #0075ff;
		--pk-shadow: 0 1px 2px rgba(20, 20, 12, 0.04), 0 24px 60px -32px rgba(20, 20, 12, 0.22);
		--pk-shadow-up: 0 1px 2px rgba(20, 20, 12, 0.05), 0 34px 70px -30px rgba(20, 20, 12, 0.32);
		color: var(--pk-text);
		color-scheme: light;
	}

	/* ── Shared bits ──────────────────────────────────────────────────── */
	.badge {
		display: inline-flex;
		align-items: center;
		gap: 0.5rem;
		height: 1.75rem;
		padding: 0 0.7rem;
		border: 1px solid var(--pk-line);
		border-radius: 999px;
		background: rgba(255, 255, 255, 0.86);
		-webkit-backdrop-filter: blur(6px);
		backdrop-filter: blur(6px);
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--pk-ink);
		white-space: nowrap;
	}
	.badge__dot {
		width: 7px;
		height: 7px;
		border-radius: 50%;
		background: var(--pk-fill);
		box-shadow: 0 0 0 3px rgba(183, 240, 76, 0.3);
	}
	.badge[data-tier='compatible'] .badge__dot {
		background: transparent;
		box-shadow: inset 0 0 0 1.5px var(--pk-muted);
	}

	.cta {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 0.45rem;
		height: 3rem;
		padding: 0 1.15rem;
		border: 1px solid var(--pk-line2);
		border-radius: 10px;
		background: var(--pk-surface);
		color: var(--pk-ink);
		font-size: 0.975rem;
		font-weight: 500;
		text-decoration: none;
		white-space: nowrap;
		transition:
			border-color var(--dur-fast) var(--ease-out),
			background-color var(--dur-fast) var(--ease-out);
	}
	.cta--fill {
		border-color: var(--pk-fill);
		background: var(--pk-fill);
	}
	.arrow {
		flex: none;
		transition: transform var(--dur-med) var(--ease-out);
	}
	.cta:focus-visible,
	.mini__text a:focus-visible,
	.code__copy:focus-visible {
		outline: 2px solid var(--pk-accent);
		outline-offset: 2px;
	}

	.code {
		position: relative;
		z-index: 1;
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 0.4rem 0.75rem;
		margin-bottom: 0.9rem;
		padding: 0.5rem 0.5rem 0.5rem 0.85rem;
		border: 1px dashed var(--pk-line2);
		border-radius: 10px;
		background: var(--pk-band);
	}
	.code__label,
	.code__until {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--pk-muted);
	}
	.code__until {
		margin-left: auto;
		padding-right: 0.35rem;
	}
	.code__field {
		display: inline-flex;
		align-items: center;
		gap: 0.6rem;
		height: 2rem;
		padding: 0 0.25rem 0 0.7rem;
		border: 1px solid var(--pk-line);
		border-radius: 7px;
		background: var(--pk-surface);
		color: var(--pk-ink);
	}
	.code__value {
		font-family: var(--font-mono);
		font-size: 0.875rem;
		font-weight: 600;
		letter-spacing: 0.04em;
	}
	.code__copy {
		min-width: 3.6rem;
		height: 1.5rem;
		padding: 0 0.45rem;
		border: 0;
		border-radius: 5px;
		background: var(--pk-hover);
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		letter-spacing: 0.06em;
		text-transform: uppercase;
		color: var(--pk-text);
		cursor: pointer;
		transition: background-color var(--dur-fast) var(--ease-out);
	}
	.code__copy:hover {
		background: rgba(14, 15, 12, 0.09);
		color: var(--pk-ink);
	}

	/* ── Full: the shelf ──────────────────────────────────────────────── */
	.picks {
		padding: clamp(0.5rem, 1.4vw, 0.875rem);
		border: 1px solid var(--pk-line);
		border-radius: 20px;
		background: var(--pk-band);
		box-shadow: var(--pk-shadow);
	}
	.picks__bar {
		display: flex;
		align-items: center;
		gap: 0.9rem;
		padding: 0.6rem 0.65rem 1rem;
	}
	.picks__logo {
		display: block;
		width: auto;
		height: 17px;
	}
	.picks__store,
	.picks__mesh {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		letter-spacing: 0.1em;
		text-transform: uppercase;
		color: var(--pk-muted);
	}
	.picks__store {
		padding-left: 0.9rem;
		border-left: 1px solid var(--pk-line2);
		line-height: 1.1;
	}
	.picks__mesh {
		margin-left: auto;
	}
	.picks__grid {
		list-style: none;
		display: grid;
		gap: clamp(0.5rem, 1.4vw, 0.875rem);
		margin: 0;
		padding: 0;
	}
	@media (min-width: 760px) {
		.picks__grid {
			grid-template-columns: repeat(2, minmax(0, 1fr));
		}
	}
	.picks__note {
		max-width: 78ch;
		margin: 0;
		padding: 1rem 0.65rem 0.5rem;
		font-size: 0.8125rem;
		line-height: 1.55;
		color: var(--pk-muted);
		text-wrap: pretty;
	}

	.pick {
		position: relative;
		display: flex;
		flex-direction: column;
		overflow: hidden;
		border: 1px solid var(--pk-line);
		border-radius: 14px;
		background: var(--pk-surface);
		transition:
			transform var(--dur-slow) var(--ease-out),
			box-shadow var(--dur-slow) var(--ease-out),
			border-color var(--dur-slow) var(--ease-out);
	}
	.pick:hover {
		transform: translateY(-2px);
		border-color: var(--pk-line2);
		box-shadow: var(--pk-shadow-up);
	}
	.pick:has(.pick__cta:focus-visible) {
		outline: 2px solid var(--pk-accent);
		outline-offset: 3px;
	}

	.pick__stage {
		position: relative;
		aspect-ratio: 16 / 11;
		overflow: hidden;
		border-bottom: 1px solid var(--pk-line);
		background:
			radial-gradient(58% 62% at 50% 46%, #ffffff 0%, rgba(255, 255, 255, 0) 72%),
			linear-gradient(180deg, #f4f4ef 0%, #e9e9e2 100%);
	}
	/* Cutting-mat grid, faded out toward the edges. */
	.pick__stage::before {
		content: '';
		position: absolute;
		inset: 0;
		background-image:
			linear-gradient(to right, var(--pk-grid) 1px, transparent 1px),
			linear-gradient(to bottom, var(--pk-grid) 1px, transparent 1px);
		background-size: 28px 28px;
		background-position: center;
		-webkit-mask-image: radial-gradient(ellipse 60% 60% at 50% 55%, #000 20%, transparent 80%);
		mask-image: radial-gradient(ellipse 60% 60% at 50% 55%, #000 20%, transparent 80%);
	}
	/* Contact shadow under the scanner. */
	.pick__stage::after {
		content: '';
		position: absolute;
		left: 50%;
		bottom: 6%;
		width: 44%;
		height: 8%;
		transform: translateX(-50%);
		background: radial-gradient(closest-side, rgba(20, 20, 12, 0.28), transparent);
	}
	.pick__stage .badge {
		position: absolute;
		top: 0.85rem;
		left: 0.85rem;
		z-index: 2;
	}
	.pick__n {
		position: absolute;
		top: 1.15rem;
		right: 1rem;
		z-index: 2;
		font-family: var(--font-mono);
		font-size: 0.75rem;
		color: var(--pk-muted);
	}
	/* Registration marks, the way a cutter frames a sheet. */
	.pick__marks i {
		position: absolute;
		width: 12px;
		height: 12px;
		border-color: var(--pk-line2);
		border-style: solid;
		border-width: 0;
	}
	.pick__marks i:nth-child(1) {
		left: 14%;
		top: 20%;
		border-top-width: 1px;
		border-left-width: 1px;
	}
	.pick__marks i:nth-child(2) {
		right: 14%;
		top: 20%;
		border-top-width: 1px;
		border-right-width: 1px;
	}
	.pick__marks i:nth-child(3) {
		left: 14%;
		bottom: 9%;
		border-bottom-width: 1px;
		border-left-width: 1px;
	}
	.pick__marks i:nth-child(4) {
		right: 14%;
		bottom: 9%;
		border-bottom-width: 1px;
		border-right-width: 1px;
	}
	.pick__img {
		position: absolute;
		left: 50%;
		bottom: 9%;
		z-index: 1;
		width: auto;
		height: 78%;
		transform: translateX(-50%);
		filter: drop-shadow(0 18px 22px rgba(20, 20, 12, 0.2))
			drop-shadow(0 3px 5px rgba(20, 20, 12, 0.12));
		transition: transform 600ms var(--ease-out);
	}
	.pick:hover .pick__img {
		transform: translateX(-50%) translateY(-6px) scale(1.025);
	}
	/* The laser pass: one sweep down the scanner each time the card is hovered. */
	.pick__laser {
		position: absolute;
		left: 10%;
		right: 10%;
		top: 12%;
		z-index: 1;
		height: 2px;
		border-radius: 2px;
		background: linear-gradient(
			90deg,
			transparent,
			var(--pk-laser) 18%,
			#8fc0ff 50%,
			var(--pk-laser) 82%,
			transparent
		);
		box-shadow: 0 0 14px 2px rgba(0, 117, 255, 0.45);
		opacity: 0;
		pointer-events: none;
	}
	.pick__laser::after {
		content: '';
		position: absolute;
		left: 0;
		right: 0;
		bottom: 2px;
		height: 44px;
		background: linear-gradient(to top, rgba(0, 117, 255, 0.14), transparent);
	}
	.pick:hover .pick__laser {
		animation: sweep 1300ms var(--ease-out) both;
	}
	@keyframes sweep {
		0% {
			top: 12%;
			opacity: 0;
		}
		12% {
			opacity: 1;
		}
		80% {
			opacity: 1;
		}
		100% {
			top: 90%;
			opacity: 0;
		}
	}

	.pick__body {
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: clamp(1.15rem, 2.2vw, 1.6rem);
	}
	.pick__role {
		margin: 0;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--pk-accent);
	}
	.pick__name {
		margin: 0.45rem 0 0;
		color: var(--pk-ink);
		font-size: clamp(1.5rem, 2.3vw, 1.85rem);
		font-weight: 600;
		line-height: 1.1;
		letter-spacing: -0.03em;
	}
	.pick__name span {
		font-weight: 500;
		color: var(--pk-muted);
	}
	.pick__blurb {
		margin: 0.75rem 0 1.25rem;
		font-size: 0.975rem;
		line-height: 1.6;
		text-wrap: pretty;
	}
	/* Pushed down so both cards' specs line up when the blurbs differ in length. */
	.pick__specs {
		margin: auto 0 1.25rem;
		border-top: 1px solid var(--pk-line);
	}
	.pick__specs div {
		display: grid;
		grid-template-columns: minmax(0, 0.9fr) minmax(0, 1.4fr);
		gap: 1rem;
		padding: 0.6rem 0;
		border-bottom: 1px solid var(--pk-line);
	}
	.pick__specs dt {
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		color: var(--pk-muted);
		padding-top: 0.15rem;
	}
	.pick__specs dd {
		margin: 0;
		font-size: 0.9375rem;
		color: var(--pk-ink);
	}
	.pick__cta {
		width: 100%;
	}
	/* The whole card is the link; the partner code sits above it. */
	.pick__cta::after {
		content: '';
		position: absolute;
		inset: 0;
	}
	.pick:hover .cta--fill {
		border-color: var(--pk-fill-hover);
		background: var(--pk-fill-hover);
	}
	.pick:hover .cta:not(.cta--fill) {
		border-color: var(--pk-muted);
		background: var(--pk-band);
	}
	.pick:hover .arrow,
	.mini__cta:hover .arrow {
		transform: translate(2px, -2px);
	}

	/* ── Compact: one card inside a guide step ────────────────────────── */
	.mini {
		display: grid;
		grid-template-columns: 7.5rem minmax(0, 1fr) auto;
		gap: 0 clamp(1rem, 2.5vw, 1.5rem);
		align-items: center;
		padding: 0.75rem clamp(0.75rem, 2vw, 1.25rem) 0.75rem 0.75rem;
		border: 1px solid var(--pk-line);
		border-radius: 14px;
		background: var(--pk-surface);
		box-shadow: var(--pk-shadow);
	}
	.mini__stage {
		position: relative;
		align-self: stretch;
		display: grid;
		place-items: center;
		min-height: 9rem;
		border-radius: 10px;
		background:
			radial-gradient(60% 60% at 50% 45%, #ffffff 0%, rgba(255, 255, 255, 0) 75%),
			linear-gradient(180deg, #f4f4ef, #e9e9e2);
	}
	.mini__stage img {
		width: auto;
		height: 7.25rem;
		filter: drop-shadow(0 10px 12px rgba(20, 20, 12, 0.22));
	}
	.mini__top {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 0.75rem;
		margin: 0;
	}
	.mini__logo {
		display: block;
		width: auto;
		height: 14px;
	}
	.mini__name {
		margin: 0.7rem 0 0;
		color: var(--pk-ink);
		font-size: 1.125rem;
		font-weight: 600;
		line-height: 1.3;
		letter-spacing: -0.015em;
		text-wrap: balance;
	}
	.mini__text {
		margin: 0.35rem 0 0.85rem;
		font-size: 0.95rem;
		line-height: 1.55;
		text-wrap: pretty;
	}
	.mini__text a {
		color: var(--pk-ink);
		text-decoration: underline;
		text-decoration-color: #a9d65a;
		text-decoration-thickness: 2px;
		text-underline-offset: 0.2em;
	}
	.mini__text a:hover {
		color: var(--pk-accent);
	}
	.mini .code {
		margin-bottom: 0.6rem;
	}
	.mini__fine {
		margin: 0;
		font-family: var(--font-mono);
		font-size: 0.6875rem;
		letter-spacing: 0.02em;
		color: var(--pk-muted);
	}
	.mini__cta:hover {
		border-color: var(--pk-fill-hover);
		background: var(--pk-fill-hover);
	}

	@media (max-width: 720px) {
		.mini {
			grid-template-columns: 5.5rem minmax(0, 1fr);
			align-items: start;
			padding: 0.75rem;
		}
		.mini__stage {
			align-self: start;
			min-height: 7rem;
		}
		.mini__stage img {
			height: 5.75rem;
		}
		.mini__cta {
			grid-column: 1 / -1;
			margin-top: 0.85rem;
		}
	}
	@media (max-width: 560px) {
		.picks__mesh {
			display: none;
		}
	}
	@media (prefers-reduced-motion: reduce) {
		.pick,
		.pick__img,
		.arrow {
			transition: none;
		}
		.pick:hover {
			transform: none;
		}
		.pick:hover .pick__img {
			transform: translateX(-50%);
		}
		.pick__laser {
			display: none;
		}
	}
</style>
